import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap-5';
import { debounce } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import Pagination from 'shared/pagination';

import { getCustomerData } from './api';

export default function SyndicationBrokerPartnerTable({ groupedOffices, customerPartnerDataPath, isParentCompany }) {
  const [partners, setPartners] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [totalPagesCount, setTotalPagesCount] = useState(1);
  const [params, setParams] = useState({ page: 1, search: '', office: '' });
  const [loading, setLoading] = useState(false);
  const stickyColumnStyles = {
    photo: { left: 0, minWidth: 50, maxWidth: 50, width: 50 },
    name: { left: 50, minWidth: 150, maxWidth: 150, width: 150 },
    office: { left: 200, minWidth: 150, maxWidth: 150, width: 150 }
  };

  useEffect(() => {
    fetchData(params);
  }, []);

  const fetchData = (requestParams) => {
    setLoading(true);
    getCustomerData(customerPartnerDataPath, requestParams).then((json) => {
      setPartners(json.partners);
      setBrokers(json.brokers);
      setTotalPagesCount(json.totalPagesCount);
      setLoading(false);
    });
  };

  const updateParams = (newParams) => {
    setParams(newParams);
    fetchData(newParams);
  };

  const handleOnPageChange = (newPage) => {
    updateParams({ ...params, page: newPage });
  };

  const handleOnRowClick = (userPath) => {
    window.location.href = userPath;
  };

  const debouncedSearch = debounce((text) => {
    updateParams({ ...params, page: 1, search: text });
  }, 400);

  const handleSearchChange = (event) => {
    debouncedSearch(event.target.value);
  };

  const handleOfficeChange = (event) => {
    updateParams({ ...params, page: 1, office: event.target.value });
  };

  const defaultOfficeOption = () => {
    return (
      <option value="">All Offices</option>
    );
  };

  const buildGroupedOfficeOptions = () => {
    if (isParentCompany) {
      return groupedOffices.map((group) => {
        return (
          <optgroup key={group.companyId} label={group.companyName}>
            { group.offices.map((office) => {
              return (
                <option key={office.id} value={office.id}>{office.name}</option>
              );
            }) }
          </optgroup>
        );
      });
    } else {
      return groupedOffices.map((office) => {
        return (
          <option key={office.id} value={office.id}>{office.name}</option>
        );
      });
    }
  };

  const partnerHeaderData = (partner) => {
    if (partner.favicon.exists) {
      return (
        <img
          alt={partner.name}
          className="d-block mx-auto"
          height="35"
          src={partner.favicon.url}
          title={partner.name}
          width="35"
        />
      );
    } else {
      return partner.name;
    }
  };

  const partnersTableHeader = () => {
    return partners.map((partner) => {
      return (
        <th className="text-center align-middle" key={partner.id} title={partner.name}>
          { partnerHeaderData(partner) }
        </th>
      );
    });
  };

  const partnerStatusClass = (status) => {
    switch (status) {
    case 'confirmed':
      return 'text-success';
    case 'unconfirmed':
      return 'text-warning';
    case 'not-present':
      return 'text-danger';
    default:
      return 'text-muted';
    }
  };

  const renderSinglePartnerBrokerStatusColumn = (user, partner) => {
    const blankStatus = { status: '', icon: '', label: '' };
    const partnerStatus = user.partnersStatus.find(ps => ps.partnerId === partner.id)?.partnerStatus || blankStatus;

    return (
      <td
        className={`text-center align-middle ${partnerStatusClass(partnerStatus.status)}`}
        key={partner.id}
        title={partnerStatus.status === 'not-present' ? 'No Credentials Provided' : ''}
      >
        <i className={`fas ${partnerStatus.icon}`} title={partnerStatus.label}></i>
      </td>
    );
  };

  const renderBrokerPartnersStatus = (user) => {
    return partners.map((partner) => {
      if (partner.onHold) {
        return (
          <td className="text-center align-middle text-warning" key={partner.id}>
            <i className="fas fa-minus-circle" title="On Hold"></i>
          </td>
        );
      } else {
        return renderSinglePartnerBrokerStatusColumn(user, partner);
      }
    });
  };

  const renderBrokers = () => {
    return brokers.map((user) => {
      return (
        <tr
          className={`${user.disabled ? 'opacity-50' : 'clickable'}`}
          key={user.id}
          onClick={user.disabled ? undefined : () => handleOnRowClick(user.editPath)}
        >
          <td
            className="position-sticky bg-body text-center align-middle"
            style={{ ...stickyColumnStyles.photo, zIndex: 2 }}
          >
            <span className="avatar avatar-lg">
              <img
                alt={user.name}
                className="avatar-image w-100 h-100 object-fit-cover"
                src={user.profilePhotoUrl}
              />
            </span>
          </td>
          <td
            className="position-sticky bg-body align-middle"
            style={{ ...stickyColumnStyles.name, zIndex: 2 }}
          >
            <strong className="d-block fw-semibold">{user.name}</strong>
            <span className="text-muted d-block">{user.jobTitle}</span>
          </td>
          <td
            className="position-sticky bg-body align-middle d-none d-sm-table-cell"
            style={{ ...stickyColumnStyles.office, zIndex: 2 }}
          >
            {user.companyOfficeLabel}
          </td>
          { renderBrokerPartnersStatus(user) }
        </tr>
      );
    });
  };

  const renderTable = () => {
    return (
      <React.Fragment>
        <div className="table-responsive">
          <table className="table table-hover w-100 mb-0">
            <thead>
              <tr>
                <th
                  className="position-sticky bg-body text-center align-middle"
                  style={{ ...stickyColumnStyles.photo, zIndex: 3 }}
                ></th>
                <th
                  className="position-sticky bg-body align-middle"
                  style={{ ...stickyColumnStyles.name, zIndex: 3 }}
                >
                  Name
                </th>
                <th
                  className="position-sticky bg-body align-middle d-none d-sm-table-cell"
                  style={{ ...stickyColumnStyles.office, zIndex: 3 }}
                >
                  Office
                </th>
                { partnersTableHeader() }
              </tr>
            </thead>
            <tbody>
              { renderBrokers() }
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-center mt-3">
          <Pagination
            currentPage={params.page}
            totalPages={totalPagesCount}
            onChange={handleOnPageChange}
          />
        </div>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <div className="overflow-hidden">
        <h6 className="mt-7 mb-4">Brokers Registered for Syndication</h6>
        <form className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
          <div className="d-none d-lg-block">
            <label className="form-label mb-1">Select Office</label>
            <Form.Select onChange={handleOfficeChange}>
              { defaultOfficeOption() }
              { buildGroupedOfficeOptions() }
            </Form.Select>
          </div>

          <div className="flex-grow-1">
            <label className="form-label mb-1 d-none d-sm-block">Search Users</label>
            <Form.Control placeholder="Search Users" type="text" onChange={handleSearchChange}/>
          </div>
        </form>

        { loading && (
          <div className="text-center py-4">
            <FontAwesomeIcon className="fa-spin" icon={faSpinner} size="xl" />
          </div>
        )}

        { brokers.length === 0 ? (
          <p className="fs-large fw-light text-muted ps-7 my-2">No brokers found</p>
        ) : renderTable() }
      </div>
    </React.Fragment>
  );
}

SyndicationBrokerPartnerTable.propTypes = {
  customerPartnerDataPath: PropTypes.string,
  groupedOffices: PropTypes.array,
  isParentCompany: PropTypes.bool,
};
